<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition duration-400 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isOpen"
        ref="modalRef"
        class="fixed inset-0 z-9999 flex h-screen w-full flex-col items-center bg-white outline-none"
        role="dialog"
        aria-modal="true"
        tabindex="0"
        :aria-label="t('common.close.image.modal')"
        @click="emit('close')"
        @keydown.arrow-left.stop="prevSlide"
        @keydown.arrow-right.stop="nextSlide"
        @keydown.escape.stop="emit('close')"
      >
        <button
          v-if="images.length > 1"
          @click.stop="prevSlide"
          class="absolute top-0 left-0 z-40 hidden h-full w-24 cursor-pointer items-center justify-center bg-white md:flex"
          :aria-label="t('common.previous.slide')"
        >
          <div
            class="flex h-6.5 w-6.5 items-center justify-center rounded-full bg-black duration-100 ease-out hover:bg-gray-medium"
          >
            <ArrowIcon class="h-3.5 w-3.5 rotate-180 text-white" />
          </div>
        </button>

        <button
          v-if="images.length > 1"
          @click.stop="nextSlide"
          class="absolute top-0 right-0 z-40 hidden h-full w-24 cursor-pointer items-center justify-center bg-white md:flex"
          aria-label="t('common.next.slide')"
        >
          <div
            class="flex h-6.5 w-6.5 items-center justify-center rounded-full bg-black duration-100 ease-out hover:bg-gray-medium"
          >
            <ArrowIcon class="h-3.5 w-3.5 text-white" />
          </div>
        </button>

        <button
          @click.stop="emit('close')"
          class="absolute top-5 right-5 z-50 cursor-pointer text-2xl text-black duration-100 ease-out before:absolute before:-inset-4 before:content-[''] hover:text-gray-medium md:top-10 md:right-10"
          :aria-label="t('common.close.image.modal')"
        >
          <XmarkIcon class="h-4.5 w-4.5" />
        </button>

        <div
          class="flex h-full w-full flex-col px-5 pt-16 pb-5 md:px-24 md:py-20"
          @click.stop
        >
          <div class="relative min-h-0 w-full flex-1">
            <Swiper
              :slides-per-view="1"
              :loop="images?.length > 1"
              @swiper="onSwiperInit"
              @slideChange="onSlideChange"
              class="h-full w-full"
            >
              <SwiperSlide
                v-for="(img, index) in images"
                :key="img.id || index"
              >
                <div
                  class="relative flex h-full w-full flex-col items-center justify-center"
                >
                  <NuxtImg
                    :src="getOptimalImageUrl(img)"
                    :alt="img.alt_text || 'Image'"
                    format="webp"
                    sizes="xs:100vw md:90vw lg:1200px xl:1400px"
                    class="h-full w-full object-contain hover:cursor-grab md:aspect-[5/2.33] md:w-auto md:max-w-full"
                    loading="lazy"
                  />
                </div>
              </SwiperSlide>
            </Swiper>
          </div>

          <div
            class="mt-5 flex w-full shrink-0 flex-col items-center md:fixed md:bottom-5 md:left-1/2 md:z-50 md:mt-0 md:max-w-301.5 md:-translate-x-1/2 md:px-5"
          >
            <div class="mb-5 flex items-center gap-5 md:hidden">
              <button
                @click.stop="prevSlide"
                class="flex h-6.5 w-6.5 cursor-pointer items-center justify-center rounded-full bg-black"
                :aria-label="t('common.previous.slide')"
              >
                <ArrowIcon class="h-3.5 w-3.5 rotate-180 text-white" />
              </button>
              <button
                @click.stop="nextSlide"
                class="flex h-6.5 w-6.5 cursor-pointer items-center justify-center rounded-full bg-black"
                :aria-label="t('common.next.slide')"
              >
                <ArrowIcon class="h-3.5 w-3.5 text-white" />
              </button>
            </div>

            <p
              class="line-clamp-3 text-center text-sm leading-[120%] text-black md:line-clamp-2"
            >
              {{ images[currentIndex]?.caption }}
            </p>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue'
import type { Swiper as SwiperClass } from 'swiper/types'
import type { ImageModalType } from '~/types'

interface Props {
  isOpen: boolean
  images: ImageModalType[]
}

const { t } = useI18n()
const props = defineProps<Props>()
const emit = defineEmits(['close'])

const modalRef = ref<HTMLElement | null>(null)
const swiperInstance = ref<SwiperClass | null>(null)
const currentIndex = ref(0)

useFocusTrap(modalRef, toRef(props, 'isOpen'), () => emit('close'))

const onSwiperInit = (swiper: SwiperClass) => {
  swiperInstance.value = swiper
}

const onSlideChange = (swiper: SwiperClass) => {
  currentIndex.value = swiper.realIndex
}

const nextSlide = () => {
  if (swiperInstance.value) {
    swiperInstance.value.slideNext()
  }
}

const prevSlide = () => {
  if (swiperInstance.value) {
    swiperInstance.value.slidePrev()
  }
}

watch(
  () => props.isOpen,
  async (newVal) => {
    if (import.meta.server) return
    if (newVal) {
      document.documentElement.style.overflow = 'hidden'
      document.body.style.overflow = 'hidden'
      await nextTick()
      modalRef.value?.focus()
    } else {
      document.documentElement.style.overflow = ''
      document.body.style.overflow = ''
    }
  },
)

onUnmounted(() => {
  if (import.meta.server) return
  document.documentElement.style.overflow = ''
  document.body.style.overflow = ''
})
</script>
